import React, { useEffect, useRef } from "react";

/**
 * Iter207.22 — Componente OTP a mini-input separati, paste-friendly.
 *
 * Props:
 *   • value: string     stringa numerica corrente (0..6 caratteri)
 *   • onChange(str)     callback con la nuova stringa
 *   • length: number    numero di celle (default 6; OpenAPI può ritornare 5-6)
 *   • autoFocus: bool   focus alla prima cella al mount
 *   • testIdPrefix: string
 *
 * Comportamenti:
 *   - Solo cifre. Ogni tasto avanza al box successivo.
 *   - Backspace su box vuoto torna al precedente.
 *   - ArrowLeft/ArrowRight per navigazione.
 *   - Paste di 5-6 cifre le distribuisce automaticamente sui box.
 *   - Su mobile: `inputMode=numeric` mostra tastierino numerico.
 */
export default function OtpInput({
  value = "",
  onChange,
  length = 6,
  autoFocus = false,
  testIdPrefix = "otp",
}) {
  const refs = useRef([]);
  const chars = Array.from({ length }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (i, ch) => {
    const next = chars.slice();
    next[i] = ch;
    onChange(next.join("").slice(0, length));
  };

  const focusIdx = (i) => {
    const el = refs.current[i];
    if (el) {
      el.focus();
      // Selezione totale per sovrascrivere velocemente
      try { el.select(); } catch { /* noop */ }
    }
  };

  const onKey = (i, e) => {
    const key = e.key;
    if (key === "Backspace") {
      e.preventDefault();
      if (chars[i]) {
        setDigit(i, "");
      } else if (i > 0) {
        setDigit(i - 1, "");
        focusIdx(i - 1);
      }
      return;
    }
    if (key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusIdx(i - 1);
      return;
    }
    if (key === "ArrowRight" && i < length - 1) {
      e.preventDefault();
      focusIdx(i + 1);
      return;
    }
    if (/^\d$/.test(key)) {
      e.preventDefault();
      setDigit(i, key);
      if (i < length - 1) focusIdx(i + 1);
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, length);
    if (!digits) return;
    e.preventDefault();
    onChange(digits);
    // Focus al box successivo all'ultimo carattere incollato
    const target = Math.min(digits.length, length - 1);
    setTimeout(() => focusIdx(target), 0);
  };

  return (
    <div className="flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-boxes`}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={chars[i]}
          onChange={() => { /* onKey gestisce input */ }}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={i === 0 ? onPaste : undefined}
          onFocus={(e) => { try { e.target.select(); } catch { /* noop */ } }}
          className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl sm:text-3xl font-mono font-semibold rounded-lg border border-slate-300 bg-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
          data-testid={`${testIdPrefix}-box-${i}`}
          aria-label={`Cifra ${i + 1}`}
        />
      ))}
    </div>
  );
}
